import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useStorageBins, useStorageLocations } from "@/hooks/useStorageBins";
import { QRLabel } from "@/components/QRLabel";
import { useTranslation } from "react-i18next";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";
import {
  resolveSparePartLabelSize,
  useLabelPrinter,
  useLabelPrintingSettings,
} from "@/labels";

export default function StorageLabelsPrint() {
  const { t } = useTranslation("inventory");
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { data: bins = [] } = useStorageBins(locationId);
  const { data: locations = [] } = useStorageLocations();
  const location = locations.find((l) => l.id === locationId);

  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { printJobs } = useLabelPrinter();
  const handleZebraPrintAll = async () => {
    const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
    await printJobs(
      bins.map((b) => ({
        kind: "storage-bin" as const,
        data: {
          qrValue: b.address,
          binCode: b.address,
          locationName: location?.name,
          description: b.description,
          footer: t("adminLabels.footer", {
            aisle: b.aisle,
            bay: b.bay,
            level: b.level,
            shelf: b.shelf,
            bin: b.bin,
          }),
        },
        options: { size },
      })),
    );
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6 print:hidden">
        <SettingsBackLink to="/settings/storage" label={t("nav.storage", { ns: "settings" })} />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("adminLabels.binLabels", { location: location?.name ?? "" })}
            </h1>
            <p className="text-muted-foreground">
              {t("adminLabels.labelsReady", { count: bins.length })}
            </p>
          </div>
          <SparePartLabelPrintMenu
            namespace="inventory"
            mode="all"
            variant="default"
            onPrintBrowser={() => window.print()}
            onPrintZebra={handleZebraPrintAll}
            label={t("adminLabels.print")}
            disabled={bins.length === 0}
          />
        </div>
      </div>

      <div className="px-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 print:grid-cols-2 print:gap-4">
          {bins.map((b) => (
            <QRLabel
              key={b.id}
              value={b.address}
              title={b.address}
              subtitle={[location?.name, b.description].filter(Boolean).join(" · ")}
              footer={t("adminLabels.footer", { aisle: b.aisle, bay: b.bay, level: b.level, shelf: b.shelf, bin: b.bin })}
            />
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 10mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
